import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import { Form } from "react-final-form";
import { LocalizationContext } from "v3/context/localization";
import { CheckoutContext } from "v3/context/checkout";
import { CheckoutErrorContext } from "v3/context/checkoutError";
import { Button } from "@pepdirect/v3/ui/Button";
import { SectionHeading } from "v3/components/checkout/SectionHeading";
import { LegalCopy } from "v3/components/checkout/LegalCopy";
import { ShippingMethodOption } from "v3/components/checkout/ShippingMethodOption";
import {
  ShippingMethod,
  useSaveShippingMethodMutation,
  useAvailableShippingMethodsQuery,
} from "v3/services/graphql/generated";
import { StyledFieldSet } from "@pepdirect/v3/ui/fields/base";
import { StyledHeading, StyledShippingMethods } from "./index.styled";
import { TenantContext } from "v3/context/tenant";

interface ShippingMethodStepProps {
  continueToNextStep: () => void;
}

type FormType = {
  shippingOptionPhoneNumber: {
    phone: string;
  };
};

export const ShippingMethodStep: FC<ShippingMethodStepProps> = ({
  continueToNextStep,
}) => {
  const { checkoutSession, isLoading, setCheckoutSession, setIsLoading } =
    useContext(CheckoutContext);
  const { clearAllErrors, handleCheckoutErrors } =
    useContext(CheckoutErrorContext);
  const { id } = checkoutSession || {};
  const { localization, formatPriceByLocale } = useContext(LocalizationContext);
  const { tenant } = useContext(TenantContext);
  const { data: shippingMethodsData, loading: shippingMethodsLoading } =
    useAvailableShippingMethodsQuery({
      variables: {
        id,
      },
    });
  const availableShippingMethods = useMemo(
    () => shippingMethodsData?.checkoutSession?.availableShippingMethods || [],
    [shippingMethodsData?.checkoutSession?.availableShippingMethods]
  );

  const [selectedShippingMethod, setSelectedShippingMethod] = useState<
    Partial<ShippingMethod> | undefined
  >(availableShippingMethods?.[0]);

  useEffect(() => {
    setIsLoading(shippingMethodsLoading);
  }, [shippingMethodsLoading, setIsLoading]);

  useEffect(() => {
    if (availableShippingMethods?.length) {
      setSelectedShippingMethod(availableShippingMethods[0]);
    }
  }, [availableShippingMethods]);

  const {
    headingText = "Shipping",
    selectShippingMethodText = "Select a shipment method",
    freeText = "Free",
  } = localization?.checkout?.shippingMethodStep || {};
  const { continueToPaymentButtonText = "Continue to Payment" } =
    localization?.checkout?.shippingStep || {};

  const [saveShippingMethod] = useSaveShippingMethodMutation();

  const onSubmit = async (values: FormType) => {
    clearAllErrors();
    setIsLoading(true);

    try {
      const { data } = await saveShippingMethod({
        variables: {
          id,
          input: {
            shippingMethodId: selectedShippingMethod?.id || "",
            code: selectedShippingMethod?.code || "",
            title: selectedShippingMethod?.title || "",
          },
        },
      });
      if (data?.saveShippingMethod) {
        setCheckoutSession(data.saveShippingMethod);
        continueToNextStep();
        setIsLoading(false);
      }
    } catch (e) {
      handleCheckoutErrors(e);
      setIsLoading(false);
    }
  };

  return (
    <>
      <Form
        keepDirtyOnReinitialize
        onSubmit={onSubmit}
        render={({ handleSubmit, form }) => (
          <form onSubmit={handleSubmit}>
            <StyledFieldSet disabled={isLoading}>
              <SectionHeading>{headingText}</SectionHeading>
              <StyledHeading size="xs" level="h3">
                {selectShippingMethodText}
              </StyledHeading>

              {availableShippingMethods && (
                <StyledShippingMethods>
                  {availableShippingMethods.map(
                    ({ locationDescription, title, rate, code }) => (
                      <ShippingMethodOption
                        checked={code === selectedShippingMethod?.code}
                        onSelect={() => {
                          if (code !== selectedShippingMethod?.code) {
                            form.reset();
                            setSelectedShippingMethod(
                              availableShippingMethods.find(
                                (item) => item.code === code
                              )
                            );
                          }
                        }}
                        name={title || ""}
                        description={locationDescription || ""}
                        price={rate ? formatPriceByLocale(rate) : freeText}
                        key={code}
                      />
                    )
                  )}
                </StyledShippingMethods>
              )}

              <Button htmlType="submit" loading={isLoading}>
                {continueToPaymentButtonText}
              </Button>
            </StyledFieldSet>
          </form>
        )}
      />
      <LegalCopy customText={tenant?.brandConfig.customTexts || []} />
    </>
  );
};
